import { Component, type ErrorInfo, type ReactNode } from 'react';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';

/**
 * One screen failing must not take the floor down with it. A technician halfway
 * through a task needs the shell to survive so they can navigate away, and the
 * message says so rather than showing a blank page.
 *
 * Sentry reporting is wired in the telemetry phase; the hook is here.
 */
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled error in a screen', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children;

    return (
      <div style={{ padding: '28px 24px', maxWidth: '38rem' }}>
        <h2 style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>
          {strings.errors.unexpected}
        </h2>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
          {strings.errors.unexpectedDetail}
        </p>
        <Button variant="primary" onClick={() => window.location.reload()}>
          {strings.errors.reload}
        </Button>
      </div>
    );
  }
}
